import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://auth.gettilted.app';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBoc2R2cmFhaG90dmNicnRtdW5qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc1NjU2MjEsImV4cCI6MjA4MzE0MTYyMX0.GzwCOtqVtaTSJVPkih_rqcUA8jsuEZLxe_bzaWSLlN8';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim() || SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() || SUPABASE_ANON_KEY;

// On native Capacitor, we handle the OAuth callback manually via deep links
// detectSessionInUrl must be OFF to prevent Supabase from also trying to parse
// the callback URL, which causes double-processing and infinite loops (PKCE codes are single-use)
const isCapacitorNative = window.Capacitor?.isNativePlatform?.() === true;
const isResetPage = window.location.pathname === '/reset-password';

// Email confirmation links must land on a real web page: email apps' in-app browsers
// block automatic redirects into custom URL schemes, so native signups use the website.
export const getEmailConfirmRedirect = () => (
  isCapacitorNative
    ? 'https://gettilted.app/auth/callback'
    : `${window.location.origin}/auth/callback`
);

// Safe storage wrapper for iOS Safari (handles private browsing mode)
const getSafeStorage = () => {
  try {
    // Test if localStorage is available and working
    const testKey = '__supabase_storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    console.log('localStorage is available');
    return window.localStorage;
  } catch (e) {
    console.warn('localStorage not available (likely iOS private browsing), using in-memory storage');

    // Fallback to in-memory storage
    let memoryStorage = {};
    return {
      getItem: (key) => memoryStorage[key] || null,
      setItem: (key, value) => { memoryStorage[key] = value; },
      removeItem: (key) => { delete memoryStorage[key]; },
      clear: () => { memoryStorage = {}; },
      length: Object.keys(memoryStorage).length,
      key: (i) => Object.keys(memoryStorage)[i] || null
    };
  }
};

export const supabase = createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: !isCapacitorNative && !isResetPage,
      flowType: 'pkce',
      storage: getSafeStorage(),
      storageKey: 'tilted-auth'
    },
    // Add global error handling
    global: {
      headers: {
        'X-Client-Info': 'tilted-web'
      }
    }
  }
);

// Lightweight public client — no auth overhead, no session restore, no token refresh.
// Used for public data queries (bars, ratings, etc.) so they load instantly
// without waiting for the auth client's initialization lock.
// Also used for password resets (implicit flow, no PKCE verifier needed).
export const supabasePublic = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false,
    flowType: 'implicit',
  }
});

export default supabase;
